import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './CustomDropdown.css';

export interface DropdownOption {
  value: string;
  label: string;
}

interface CustomDropdownProps {
  value: string;
  placeholder: string;
  options: DropdownOption[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
  emptyText?: string;
  containerRef?: React.RefObject<HTMLDivElement>;
  className?: string;
  disabled?: boolean;
}

const CustomDropdown = ({
  value,
  placeholder,
  options,
  isOpen,
  onToggle,
  onSelect,
  emptyText = 'Sin opciones disponibles',
  containerRef,
  className = '',
  disabled = false
}: CustomDropdownProps): JSX.Element => {
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const selectedLabel = options.find((opt) => opt.value === value)?.label || placeholder;

  useEffect(() => {
    if (!isOpen || !rootRef.current) return;

    const updatePosition = (): void => {
      if (!rootRef.current) return;
      const rect = rootRef.current.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 6,
        left: rect.left,
        width: rect.width,
        zIndex: 9999
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);

    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const setDropdownRef = (node: HTMLDivElement | null): void => {
    rootRef.current = node;
    if (containerRef) {
      (containerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  };

  return (
    <div className={`custom-dropdown ${className}`.trim()} ref={setDropdownRef}>
      <button
        type="button"
        className={`custom-dropdown-toggle ${isOpen ? 'active' : ''}`}
        onClick={() => {
          if (!disabled) onToggle();
        }}
        disabled={disabled}
      >
        <span>{selectedLabel}</span>
        <svg className="custom-dropdown-arrow" viewBox="0 0 1024 1024">
          <path d="M426 726v-428l214 214z"></path>
        </svg>
      </button>
      {isOpen && createPortal(
        <div
          className="custom-dropdown-menu"
          style={menuStyle}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {options.length === 0 ? (
            <div className="custom-dropdown-empty">{emptyText}</div>
          ) : (
            options.map((opt) => (
              <button
                key={opt.value}
                type="button"
                className={`custom-dropdown-item ${value === opt.value ? 'active' : ''}`}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => onSelect(opt.value)}
              >
                {opt.label}
              </button>
            ))
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default CustomDropdown;
